import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { useAdvisorAccess } from '../features/auth/hooks/useAdvisorAccess';
import AccessDenied from '../features/auth/components/AccessDenied';
import SetupRequired from '../features/auth/components/SetupRequired';
import { CircularProgress, Box } from '@mui/material';
import { User, OrgAssignment } from '../services/userService';

interface UserAccessContextType {
  customerIds: string[];
  isAdmin: boolean;
  isLoading: boolean;
  canAccessCustomerId: (customerId: string) => boolean;
  hasAccess: boolean;
  hasSystemAccess: boolean;
  needsSetup: boolean;
  isDashboardAdmin: boolean;
  isDashboardAdvisor: boolean;
  azureGroups: string[];
  role?: string;
  permissions?: User['permissions'];
  user?: User | null;
  orgAssignments: OrgAssignment[];
  hasEnterpriseView: boolean;
  // Enterprise "view as advisor": while active, the whole dashboard scopes to
  // that one advisor's book (customerIds/isAdmin flags are overridden).
  viewAsCustomerId: string | null;
  viewAsAdvisorName: string | null;
  enterViewAs: (customerId: string, advisorName?: string) => void;
  exitViewAs: () => void;
  // Re-fetch the DB user record (e.g. after a self-service profile update).
  refreshUser: () => Promise<void>;
}

const VIEW_AS_STORAGE_KEY = 'enterpriseViewAs';

const readStoredViewAs = (): { customerId: string; name?: string } | null => {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.customerId === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

const UserAccessContext = createContext<UserAccessContextType | undefined>(undefined);

interface UserAccessProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the app and provides user access information
 * throughout the component tree
 */
export const UserAccessProvider: React.FC<UserAccessProviderProps> = ({ children }) => {
  const access = useAdvisorAccess();
  const [viewAs, setViewAs] = useState<{ customerId: string; name?: string } | null>(
    readStoredViewAs
  );

  const enterViewAs = useCallback((customerId: string, advisorName?: string) => {
    const next = { customerId, name: advisorName };
    try {
      sessionStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage unavailable — mode still works for this render tree.
    }
    setViewAs(next);
  }, []);

  const exitViewAs = useCallback(() => {
    try {
      sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
    } catch {
      // ignore
    }
    setViewAs(null);
  }, []);

  // Show loading spinner while checking access
  if (access.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show setup required if user has Auth0 access but not in database
  if (access.needsSetup) {
    return <SetupRequired />;
  }

  // Show access denied if user doesn't have system access
  if (!access.hasAccess) {
    return <AccessDenied />;
  }

  // View-as is only honored for enterprise-scoped users; the server enforces
  // the same boundary (org scope folded into the customer-access middleware).
  const viewAsActive = !!viewAs && access.hasEnterpriseView;

  const value: UserAccessContextType = viewAsActive
    ? {
        ...access,
        customerIds: [viewAs.customerId],
        isAdmin: false,
        isDashboardAdmin: false,
        isDashboardAdvisor: true,
        canAccessCustomerId: (customerId: string) => customerId === viewAs.customerId,
        viewAsCustomerId: viewAs.customerId,
        viewAsAdvisorName: viewAs.name ?? null,
        enterViewAs,
        exitViewAs,
      }
    : {
        ...access,
        viewAsCustomerId: null,
        viewAsAdvisorName: null,
        enterViewAs,
        exitViewAs,
      };

  return (
    <UserAccessContext.Provider value={value}>
      {children}
    </UserAccessContext.Provider>
  );
};

/**
 * Hook to access user permissions and customer ID access
 */
export const useUserAccess = (): UserAccessContextType => {
  const context = useContext(UserAccessContext);
  if (context === undefined) {
    throw new Error('useUserAccess must be used within a UserAccessProvider');
  }
  return context;
};